import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import './ChangelogTab.css';

// Type → label + colour. Muted tints, so a page of badges reads as a list
// rather than a quilt.
const TYPE_META = {
  feat:     { label: 'Feature',  color: '#3F5A37', bg: '#E4EAE0' },
  fix:      { label: 'Fix',      color: '#B04E28', bg: '#F0E5DF' },
  security: { label: 'Security', color: '#8A6A1A', bg: '#F3ECD9' },
  docs:     { label: 'Docs',     color: '#3A6A9A', bg: '#E3ECF3' },
  refactor: { label: 'Refactor', color: '#6A4A8A', bg: '#ECE6F3' },
  db:       { label: 'Database', color: '#2F7A82', bg: '#E0F0F1' },
  test:     { label: 'Tests',    color: '#5A7A5A', bg: '#E8EFE8' },
  infra:    { label: 'Infra',    color: '#6A6A6A', bg: '#ECECEA' },
  chore:    { label: 'Chore',    color: '#8A8880', bg: '#EFEEEA' },
};
const TYPE_ORDER = ['feat', 'fix', 'security', 'refactor', 'db', 'docs', 'test', 'infra', 'chore'];

const REPO_FALLBACK = 'https://github.com/Radius-of-Self/paradigm_sdk';

function relativeDate(dateStr) {
  const then = new Date(dateStr + 'T00:00:00');
  const days = Math.round((Date.now() - then.getTime()) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return days + ' days ago';
  if (days < 30) return Math.floor(days / 7) + 'w ago';
  if (days < 365) return Math.floor(days / 30) + 'mo ago';
  return Math.floor(days / 365) + 'y ago';
}

function Entry({ entry, repoUrl }) {
  const [open, setOpen] = useState(false);
  const meta = TYPE_META[entry.type] || TYPE_META.chore;
  const hasBody = entry.body && entry.body.length > 0;
  const commitUrl = entry.full_hash ? `${repoUrl}/commit/${entry.full_hash}` : null;

  return (
    <div className={`releases-entry${open ? ' open' : ''}`}>
      <div className="releases-entry-head">
        <span className="releases-badge" style={{ color: meta.color, background: meta.bg }}>{meta.label}</span>
        <span className="releases-entry-title">{entry.title}</span>
        {commitUrl
          ? <a className="releases-entry-hash" href={commitUrl} target="_blank" rel="noopener noreferrer">{entry.hash} ↗</a>
          : <span className="releases-entry-hash">{entry.hash}</span>}
      </div>
      {hasBody && (
        <>
          <button type="button" className="releases-entry-toggle" onClick={() => setOpen(o => !o)}>
            <svg viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"><path d="M3 2l4 3-4 3" /></svg>
            Details
          </button>
          {open && (
            <div className="releases-entry-body">
              {entry.body.map((line, i) => (
                line === ''
                  ? <div key={i} className="releases-entry-body-line spacer" />
                  : <div key={i} className="releases-entry-body-line">{line}</div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function ChangelogTab() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getChangelog()
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const entries = data?.entries || [];
  const repoUrl = data?.repo_url || REPO_FALLBACK;

  const counts = {};
  entries.forEach(e => { counts[e.type] = (counts[e.type] || 0) + 1; });
  const pills = [{ key: 'all', label: 'All', count: entries.length }];
  TYPE_ORDER.forEach(t => { if (counts[t]) pills.push({ key: t, label: TYPE_META[t].label, count: counts[t] }); });

  const filtered = filter === 'all' ? entries : entries.filter(e => e.type === filter);

  // Group consecutive entries by date (data is newest-first).
  const groups = [];
  filtered.forEach(e => {
    const last = groups[groups.length - 1];
    if (last && last.date === e.date) last.items.push(e);
    else groups.push({ date: e.date, items: [e] });
  });

  return (
    <div className="releases">
      {loading ? (
        <div className="releases-empty">Loading…</div>
      ) : entries.length === 0 ? (
        <div className="releases-empty">No release notes yet.</div>
      ) : (
        <>
          <div className="releases-filters" role="group" aria-label="Filter by type">
            {pills.map(p => (
              <button
                type="button"
                key={p.key}
                className="releases-pill"
                data-active={p.key === filter || undefined}
                aria-pressed={p.key === filter}
                onClick={() => setFilter(p.key)}
              >
                {p.label}<span className="releases-pill-count">{p.count}</span>
              </button>
            ))}
          </div>

          {groups.length === 0 ? (
            <div className="releases-empty">Nothing under this filter yet.</div>
          ) : groups.map((g, gi) => (
            <section className="releases-date-group" key={`${g.date}-${gi}`}>
              <div className="releases-date-col">
                <div className="releases-date-label">{g.date}</div>
                <div className="releases-date-rel">{relativeDate(g.date)}</div>
              </div>
              <div className="releases-entry-col">
                {g.items.map((e, i) => <Entry key={`${e.hash}-${i}`} entry={e} repoUrl={repoUrl} />)}
              </div>
            </section>
          ))}
        </>
      )}
    </div>
  );
}
